import axiosAuthClient from "@/common/services/axios";

export interface PushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

interface StoredPushSubscription extends PushSubscription {
  id: string;
}

interface PushPublicKeyResponse {
  publicKey: string;
}

export interface PushNotificationsAvailability {
  supported: boolean;
  reason?: string;
}

const PUSH_NOTIFICATIONS_UNSUPPORTED_MESSAGE =
  "Este navegador no soporta notificaciones. Descarga o instala la app para poder usarlas.";

interface SubscribeToPushNotificationsOptions {
  requestPermission?: boolean;
}

type InitializePushNotificationsOptions = SubscribeToPushNotificationsOptions;

const PUSH_REGISTERED_ENDPOINT_STORAGE_KEY_PREFIX = "push_registered_endpoint";
const PUSH_PROMPT_DISMISSED_STORAGE_KEY_PREFIX = "push_prompt_dismissed";
const initializePushNotificationsPromises = new Map<string, Promise<boolean>>();
const PUSH_STATE_CHANGED_EVENT = "pushStateChanged";
const SHOW_PUSH_ENABLE_PROMPT_EVENT = "showPushEnablePrompt";

function getPushRegisteredEndpointStorageKey(userId: string): string {
  return `${PUSH_REGISTERED_ENDPOINT_STORAGE_KEY_PREFIX}:${userId}`;
}

export function getPushPromptDismissedStorageKey(userId: string): string {
  return `${PUSH_PROMPT_DISMISSED_STORAGE_KEY_PREFIX}:${userId}`;
}

export function hasPushPromptDismissedState(userId: string): boolean {
  if (!userId) {
    return false;
  }

  return Boolean(localStorage.getItem(getPushPromptDismissedStorageKey(userId)));
}

export function markPushPromptDismissedState(userId: string): void {
  if (!userId) {
    return;
  }

  localStorage.setItem(getPushPromptDismissedStorageKey(userId), "1");
}

export function clearPushPromptDismissedState(userId: string): void {
  if (!userId) {
    return;
  }

  localStorage.removeItem(getPushPromptDismissedStorageKey(userId));
}

function dispatchPushStateChangedEvent(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(PUSH_STATE_CHANGED_EVENT));
}

export function getPushStateChangedEventName(): string {
  return PUSH_STATE_CHANGED_EVENT;
}

export function getShowPushEnablePromptEventName(): string {
  return SHOW_PUSH_ENABLE_PROMPT_EVENT;
}

export function requestPushEnablePrompt(userId: string): Promise<boolean> {
  if (typeof window === "undefined" || !userId) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    window.dispatchEvent(
      new CustomEvent(SHOW_PUSH_ENABLE_PROMPT_EVENT, {
        detail: {
          userId,
          resolve,
        },
      }),
    );
  });
}

/**
 * Convierte una clave base64 URL-safe a Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function uint8ArrayToUrlBase64(value: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...value));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function getPushPublicKey(): Promise<string> {
  const { data } = await axiosAuthClient.get<PushPublicKeyResponse>("/notifications/push");
  const publicKey = data?.publicKey?.trim();
  if (!publicKey) {
    throw new Error("Push public key not provided by server");
  }

  return publicKey;
}

/**
 * Solicita permiso para mostrar notificaciones
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!("Notification" in window)) {
    throw new Error(PUSH_NOTIFICATIONS_UNSUPPORTED_MESSAGE);
  }

  if (Notification.permission === "granted") {
    return Notification.permission;
  }

  if (Notification.permission === "denied") {
    return Notification.permission;
  }

  const permission = await Notification.requestPermission();
  dispatchPushStateChangedEvent();
  return permission;
}

function isIosDevice(): boolean {
  return (
    /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
    (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1)
  );
}

function isStandalonePwa(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * Verifica si el contexto es seguro para service workers (HTTPS o localhost)
 */
function isSecureContext(): boolean {
  return (
    window.isSecureContext ||
    window.location.protocol === "https:" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "[::1]"
  );
}

function getPushServiceWorkerUrl(): string {
  const baseUrl = process.env.BASE_URL || "/";
  return `${baseUrl}${
    process.env.NODE_ENV === "production" ? "service-worker.js" : "service-worker-custom.js"
  }`;
}

export function getPushNotificationsAvailability(): PushNotificationsAvailability {
  if (!isSecureContext()) {
    return {
      supported: false,
      reason: "Las notificaciones push requieren HTTPS o localhost.",
    };
  }

  if (!("Notification" in window)) {
    return {
      supported: false,
      reason: PUSH_NOTIFICATIONS_UNSUPPORTED_MESSAGE,
    };
  }

  if (!("serviceWorker" in navigator)) {
    return {
      supported: false,
      reason: "Este navegador no soporta service workers.",
    };
  }

  if (!("PushManager" in window)) {
    return {
      supported: false,
      reason:
        "Este entorno no soporta Web Push. Si usas la app nativa, " +
        "necesitas configurar push nativo para iOS y Android.",
    };
  }

  if (isIosDevice() && !isStandalonePwa()) {
    return {
      supported: false,
      reason:
        "En iPhone y iPad debes añadir la app a la pantalla de inicio " +
        "antes de activar las notificaciones.",
    };
  }

  return { supported: true };
}

/**
 * Suscribe al usuario al servicio de notificaciones push
 */
export async function subscribeToPushNotifications(
  options: SubscribeToPushNotificationsOptions = {},
): Promise<PushSubscription | null> {
  try {
    const availability = getPushNotificationsAvailability();
    if (!availability.supported) {
      console.warn(availability.reason);
      return null;
    }

    if (Notification.permission !== "granted") {
      if (Notification.permission === "denied") {
        console.warn("Permisos de notificación denegados");
        return null;
      }

      if (!options.requestPermission) {
        console.info(
          "Las notificaciones push requieren una interacción del usuario para solicitar permisos",
        );
        return null;
      }

      const permission = await requestNotificationPermission();
      if (permission !== "granted") {
        console.warn("Permiso de notificaciones no concedido");
        return null;
      }
    }

    const hasServiceWorkerSupport = "serviceWorker" in navigator;
    if (!hasServiceWorkerSupport) {
      return null;
    }

    const vapidPublicKey = await getPushPublicKey();
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey) as unknown as BufferSource;

    // Asegurar que el service worker esté registrado
    let registration: ServiceWorkerRegistration;

    try {
      // Primero verificar si ya hay un service worker registrado
      const existingRegistration = await navigator.serviceWorker.getRegistration();
      if (existingRegistration && existingRegistration.active) {
        registration = existingRegistration;
        console.log("Service Worker ya está registrado y activo");
      } else {
        // Intentar registrar el service worker adecuado al entorno
        try {
          const swUrl = getPushServiceWorkerUrl();
          console.log("Registrando Service Worker desde:", swUrl);
          registration = await navigator.serviceWorker.register(swUrl, {
            scope: process.env.BASE_URL || "/",
          });
          // Esperar a que esté activo
          await navigator.serviceWorker.ready;
          console.log("Service Worker registrado y activo");
        } catch (swError: any) {
          console.error("Error al registrar service worker:", swError);
          // Intentar obtener cualquier service worker existente
          registration = await navigator.serviceWorker.ready;
        }
      }
    } catch (error: any) {
      console.error("Error al obtener/registrar service worker:", error);
      throw new Error(
        `No se pudo acceder al Service Worker: ${error.message || "Error desconocido"}`,
      );
    }

    // Verificar que el registration tenga pushManager
    if (!registration.pushManager) {
      throw new Error("PushManager no está disponible en el service worker");
    }

    // Reuse existing device subscription when available
    let subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const currentApplicationServerKey = subscription.options.applicationServerKey;
      if (!currentApplicationServerKey) {
        await subscription.unsubscribe();
        subscription = null;
      } else {
        const currentKeyBase64 = uint8ArrayToUrlBase64(new Uint8Array(currentApplicationServerKey));
        if (currentKeyBase64 !== vapidPublicKey) {
          await subscription.unsubscribe();
          subscription = null;
        }
      }
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }

    // Convertir la suscripción al formato que espera el backend
    const p256dhKey = subscription.getKey("p256dh");
    const authKey = subscription.getKey("auth");

    if (!p256dhKey || !authKey) {
      throw new Error("Subscription keys not found");
    }

    const pushSubscription: PushSubscription = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dhKey))),
        auth: btoa(String.fromCharCode(...new Uint8Array(authKey))),
      },
    };

    return pushSubscription;
  } catch (error: any) {
    console.error("Error al suscribirse a push notifications:", error);
    // Proporcionar mensajes más específicos
    if (error.name === "NotAllowedError") {
      console.error("Permisos de notificación denegados por el usuario");
    } else if (error.name === "NotSupportedError") {
      console.error("Push notifications no están soportadas en este navegador o contexto");
    } else if (error.message) {
      console.error("Detalle del error:", error.message);
    }
    return null;
  }
}

/**
 * Envía la suscripción push al backend
 */
export async function registerPushSubscription(subscription: PushSubscription): Promise<void> {
  await axiosAuthClient.post("/users/me/subscriptions", subscription);
}

async function listPushSubscriptions(): Promise<StoredPushSubscription[]> {
  const { data } = await axiosAuthClient.get<StoredPushSubscription[]>("/users/me/subscriptions");
  return data;
}

async function removePushSubscription(subscriptionId: string): Promise<void> {
  await axiosAuthClient.delete(`/users/me/subscriptions/${subscriptionId}`);
}

/**
 * Ensures the current endpoint exists once for the logged user.
 * This prevents duplicate PushEvent deliveries caused by duplicated subscriptions.
 */
async function syncPushSubscription(subscription: PushSubscription): Promise<void> {
  const subscriptions = await listPushSubscriptions();
  const sameEndpointSubscriptions = subscriptions.filter(
    (userSubscription) => userSubscription.endpoint === subscription.endpoint,
  );

  if (sameEndpointSubscriptions.length === 0) {
    await registerPushSubscription(subscription);
    return;
  }

  const duplicatedSubscriptions = sameEndpointSubscriptions.slice(1);
  if (duplicatedSubscriptions.length === 0) {
    return;
  }

  await Promise.all(
    duplicatedSubscriptions.map((duplicatedSubscription) =>
      removePushSubscription(duplicatedSubscription.id),
    ),
  );
}

async function unsubscribeBrowserPushManager(): Promise<void> {
  try {
    if (!("serviceWorker" in navigator)) return;
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.pushManager) return;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
  } catch (error) {
    console.warn("Error unsubscribing from browser PushManager:", error);
  }
}

async function getCurrentBrowserPushSubscription(): Promise<globalThis.PushSubscription | null> {
  try {
    if (!("serviceWorker" in navigator)) return null;
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.pushManager) return null;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.warn("Error getting current browser PushManager subscription:", error);
    return null;
  }
}

export async function clearPushNotificationsForUser(userId: string): Promise<void> {
  if (!userId) {
    return;
  }

  const storageKey = getPushRegisteredEndpointStorageKey(userId);
  const storedEndpoint = localStorage.getItem(storageKey);

  try {
    const browserSubscription = await getCurrentBrowserPushSubscription();
    const endpoint = storedEndpoint || browserSubscription?.endpoint;

    if (endpoint) {
      const subscriptions = await listPushSubscriptions();
      const subscriptionsToRemove = subscriptions.filter(
        (userSubscription) => userSubscription.endpoint === endpoint,
      );

      if (subscriptionsToRemove.length > 0) {
        await Promise.all(
          subscriptionsToRemove.map((subscriptionToRemove) =>
            removePushSubscription(subscriptionToRemove.id),
          ),
        );
      }
    }

    if (browserSubscription) {
      await browserSubscription.unsubscribe();
    } else {
      await unsubscribeBrowserPushManager();
    }
  } catch (error) {
    console.error("Error clearing push notifications for user:", error);
  } finally {
    localStorage.removeItem(storageKey);
    dispatchPushStateChangedEvent();
  }
}

/**
 * Inicializa las notificaciones push para el usuario
 */
export async function initializePushNotifications(
  userId: string,
  options: InitializePushNotificationsOptions = {},
): Promise<boolean> {
  if (!userId) {
    throw new Error("userId is required to initialize push notifications");
  }

  const existingPromise = initializePushNotificationsPromises.get(userId);
  if (existingPromise) {
    return existingPromise;
  }

  const initializePromise = (async () => {
    try {
      const subscription = await subscribeToPushNotifications(options);
      if (!subscription) {
        return false;
      }

      const storageKey = getPushRegisteredEndpointStorageKey(userId);
      await syncPushSubscription(subscription);
      localStorage.setItem(storageKey, subscription.endpoint);
      dispatchPushStateChangedEvent();
      console.log("Push notifications inicializadas correctamente");
      return true;
    } catch (error) {
      console.error("Error al inicializar push notifications:", error);
      localStorage.removeItem(getPushRegisteredEndpointStorageKey(userId));
      return false;
    }
  })();
  initializePushNotificationsPromises.set(userId, initializePromise);

  try {
    return await initializePromise;
  } finally {
    initializePushNotificationsPromises.delete(userId);
  }
}
